/**
 * Client ของป้าย Kanban การผลิต — พูดกับ `/v1/output-tags` ตาม
 * `apps/api/src/cap_erp_api/modules/production_tag/output_tag_router.py` (D-169, D-170)
 *
 * หลักการเดียวกับ `lib/productionOrders/client.ts`:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 * - payload ที่ผิดรูปคือ `invalid-response` ไม่ใช่ "ไม่มีป้าย"
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และไม่เขียนอะไรลง storage เลย
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 */

import type {
  OutputTag,
  OutputTagListPage,
  OutputTagPrintData,
  OutputTagProcessFlowStep,
  PrintOutputTagBody,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

export const OUTPUT_TAGS_PATH = '/v1/output-tags';

export function outputTagPath(tagCode: string): string {
  return `${OUTPUT_TAGS_PATH}/${encodeURIComponent(tagCode)}`;
}

export function outputTagReprintPath(tagCode: string): string {
  return `${outputTagPath(tagCode)}/reprint`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ contract นิยามไว้ แยกเป็นสถานะของตัวเอง — ไม่ยุบ
 * `403`/`404`/`409`/`422` เข้าด้วยกันโดยเจตนา ด้วยเหตุผลเดียวกับ
 * `ProductionOrderFailure`
 */
export type OutputTagFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type OutputTagListResult =
  | { readonly status: 'ready'; readonly page: OutputTagListPage }
  | OutputTagFailure;

export type OutputTagResult = { readonly status: 'ready'; readonly tag: OutputTag } | OutputTagFailure;

export interface OutputTagRequestOptions {
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /** inject ได้เพื่อการทดสอบ — default คือ `apiFetch` เหมือน client อื่นทุกตัว */
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** ฟิลด์ที่ nullable ได้ — `undefined` แปลว่า "ผิดรูป" ด้วยเหตุผลเดียวกับ client อื่น */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

function positiveInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}

/** จำนวนเต็มบวกที่ nullable ได้ — `undefined` แปลว่าผิดชนิด เหมือน `nullableString` */
function nullablePositiveInteger(value: unknown): number | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  const parsed = positiveInteger(value);
  return parsed === null ? undefined : parsed;
}

function requiredBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

export function parseOutputTagProcessFlowStep(raw: unknown): OutputTagProcessFlowStep | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const seq = positiveInteger(record.operation_seq);
  if (seq === null) {
    return null;
  }
  const workCenterCode = nullableString(record.work_center_code);
  const workCenterName = nullableString(record.work_center_name);
  const current = requiredBoolean(record.current);
  if (workCenterCode === undefined || workCenterName === undefined || current === undefined) {
    return null;
  }

  return {
    operation_seq: seq,
    work_center_code: workCenterCode,
    work_center_name: workCenterName,
    current,
  };
}

/**
 * `print_data` — ตรวจเฉพาะฟิลด์ที่ `KanbanTagSheet` ใช้จริง (D-170 invariant) ไม่ใช่
 * schema เต็มรูปแบบ เพราะฝั่งเซิร์ฟเวอร์ประกาศเป็น `Dict[str, Any]` ที่เปิดกว้างไว้
 * โดยเจตนา (D-170 ข้อ 3 — เก็บสิ่งที่พิมพ์จริงไว้พิมพ์ซ้ำได้)
 */
export function parseOutputTagPrintData(raw: unknown): OutputTagPrintData | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const plantCode = nullableString(record.plant_code);
  const orderNo = nullableString(record.order_no);
  const operationSeq =
    record.operation_seq === null || record.operation_seq === undefined
      ? null
      : positiveInteger(record.operation_seq);
  if (operationSeq === null && record.operation_seq !== null && record.operation_seq !== undefined) {
    return null;
  }
  const partNo = nullableString(record.part_no);
  const partName = nullableString(record.part_name);
  const model = nullableString(record.model);
  const planningCode = nullableString(record.planning_code);
  const tagColor = nullableString(record.tag_color);
  const line = nullableString(record.line);
  const lineName = nullableString(record.line_name);
  const qtyPerPkg = nullableString(record.qty_per_pkg);
  const qtyUom = nullableString(record.qty_uom);
  const pkgType = nullableString(record.pkg_type);
  const packageSeq = nullablePositiveInteger(record.package_seq);
  const packageCount = nullablePositiveInteger(record.package_count);
  const tagQty = nullableString(record.tag_qty);
  const stage = nullableString(record.stage);
  const storeLocation = nullableString(record.store_location);
  const processLeadtime = nullableString(record.process_leadtime);
  const processLeadtimeBasis = nullableString(record.process_leadtime_basis);
  const imageId = nullableString(record.image_id);
  const tagCode = nullableString(record.tag_code);
  const lotNo = nullableString(record.lot_no);
  const qr = nullableString(record.qr);
  const printedAt = nullableString(record.printed_at);
  const reprintOf = nullableString(record.reprint_of);
  if (
    plantCode === undefined ||
    orderNo === undefined ||
    partNo === undefined ||
    partName === undefined ||
    model === undefined ||
    planningCode === undefined ||
    tagColor === undefined ||
    line === undefined ||
    lineName === undefined ||
    qtyPerPkg === undefined ||
    qtyUom === undefined ||
    pkgType === undefined ||
    packageSeq === undefined ||
    packageCount === undefined ||
    tagQty === undefined ||
    stage === undefined ||
    storeLocation === undefined ||
    processLeadtime === undefined ||
    processLeadtimeBasis === undefined ||
    imageId === undefined ||
    tagCode === undefined ||
    lotNo === undefined ||
    qr === undefined ||
    printedAt === undefined ||
    reprintOf === undefined
  ) {
    return null;
  }

  const rawFlow = record.process_flow;
  const processFlow: OutputTagProcessFlowStep[] = [];
  if (rawFlow !== undefined && rawFlow !== null) {
    if (!Array.isArray(rawFlow)) {
      return null;
    }
    for (const entry of rawFlow) {
      const parsed = parseOutputTagProcessFlowStep(entry);
      if (parsed === null) {
        return null;
      }
      processFlow.push(parsed);
    }
  }

  return {
    plant_code: plantCode,
    order_no: orderNo,
    operation_seq: operationSeq,
    part_no: partNo,
    part_name: partName,
    model,
    planning_code: planningCode,
    tag_color: tagColor,
    line,
    line_name: lineName,
    process_flow: processFlow,
    qty_per_pkg: qtyPerPkg,
    qty_uom: qtyUom,
    pkg_type: pkgType,
    package_seq: packageSeq,
    package_count: packageCount,
    tag_qty: tagQty,
    stage,
    store_location: storeLocation,
    process_leadtime: processLeadtime,
    process_leadtime_basis: processLeadtimeBasis,
    image_id: imageId,
    tag_code: tagCode,
    lot_no: lotNo,
    qr,
    printed_at: printedAt,
    reprint_of: reprintOf,
  };
}

export function parseOutputTag(raw: unknown): OutputTag | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const tagCode = requiredString(record.tag_code);
  const status = requiredString(record.status);
  const stage = requiredString(record.stage);
  const productionOrderId = requiredString(record.production_order_id);
  const operationSeq = positiveInteger(record.operation_seq);
  const lotNo = requiredString(record.lot_no);
  const printedByEmployeeId = requiredString(record.printed_by_employee_id);
  const printedAt = requiredString(record.printed_at);
  const packageSeq = positiveInteger(record.package_seq);
  const packageCount = positiveInteger(record.package_count);
  const tagQty = requiredString(record.tag_qty);
  if (
    tagCode === null ||
    status === null ||
    stage === null ||
    productionOrderId === null ||
    operationSeq === null ||
    lotNo === null ||
    printedByEmployeeId === null ||
    printedAt === null ||
    packageSeq === null ||
    packageCount === null ||
    tagQty === null
  ) {
    return null;
  }

  const plantCode = nullableString(record.plant_code);
  const lotId = nullableString(record.lot_id);
  const replacedByTagCode = nullableString(record.replaced_by_tag_code);
  const qtyPerPkg = nullableString(record.qty_per_pkg);
  if (
    plantCode === undefined ||
    lotId === undefined ||
    replacedByTagCode === undefined ||
    qtyPerPkg === undefined
  ) {
    return null;
  }

  const printData = parseOutputTagPrintData(record.print_data);
  if (printData === null) {
    return null;
  }

  return {
    tag_code: tagCode,
    status,
    stage,
    plant_code: plantCode,
    production_order_id: productionOrderId,
    operation_seq: operationSeq,
    lot_no: lotNo,
    lot_id: lotId,
    replaced_by_tag_code: replacedByTagCode,
    qty_per_pkg: qtyPerPkg,
    package_seq: packageSeq,
    package_count: packageCount,
    tag_qty: tagQty,
    printed_by_employee_id: printedByEmployeeId,
    printed_at: printedAt,
    print_data: printData,
  };
}

export function parseOutputTagListPage(raw: unknown): OutputTagListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const rawItems = (raw as Record<string, unknown>).items;
  if (!Array.isArray(rawItems)) {
    return null;
  }

  const items: OutputTag[] = [];
  for (const entry of rawItems) {
    const parsed = parseOutputTag(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  return { items };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    details,
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult = { readonly status: 'payload'; readonly payload: unknown } | OutputTagFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: OutputTagRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/** `GET /v1/output-tags?production_order_id=` — ป้ายทุกใบของใบสั่งผลิตใบนี้ */
export async function listOutputTags(
  productionOrderId: string,
  options: OutputTagRequestOptions = {},
): Promise<OutputTagListResult> {
  const query = new URLSearchParams({ production_order_id: productionOrderId });
  const result = await requestPayload(
    `${OUTPUT_TAGS_PATH}?${query.toString()}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseOutputTagListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/** `GET /v1/output-tags/{tag_code}` — ป้ายเดียว พร้อม `print_data` ที่พิมพ์จริง */
export async function getOutputTag(
  tagCode: string,
  options: OutputTagRequestOptions = {},
): Promise<OutputTagResult> {
  const result = await requestPayload(
    outputTagPath(tagCode),
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const tag = parseOutputTag(result.payload);
  return tag === null ? { status: 'invalid-response' } : { status: 'ready', tag };
}

/**
 * `POST /v1/output-tags` — พิมพ์ป้ายของ operation หนึ่งขั้น ต้องการ `PRODUCTION_ORDER`
 * + `CREATE` — คืน **รายการ** ป้าย หนึ่งป้ายต่อหนึ่งแพ็คเกจที่ยังไม่มีป้าย ACTIVE
 * (D-170 ข้อ 4) ไม่ใช่ป้ายเดี่ยวอีกต่อไป
 */
export async function printOutputTag(
  body: PrintOutputTagBody,
  options: OutputTagRequestOptions = {},
): Promise<OutputTagListResult> {
  const result = await requestPayload(
    OUTPUT_TAGS_PATH,
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        production_order_id: body.production_order_id,
        operation_seq: body.operation_seq,
      }),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseOutputTagListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * `POST /v1/output-tags/{tag_code}/reprint` — ป้ายเก่ากลายเป็น `REPRINTED` และผูกไปยัง
 * ป้ายใหม่ที่คืนมา (lot เดิม ข้อมูลพิมพ์เดิม)
 */
export async function reprintOutputTag(
  tagCode: string,
  options: OutputTagRequestOptions = {},
): Promise<OutputTagResult> {
  const result = await requestPayload(
    outputTagReprintPath(tagCode),
    { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const tag = parseOutputTag(result.payload);
  return tag === null ? { status: 'invalid-response' } : { status: 'ready', tag };
}
